import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View, StyleSheet, ActivityIndicator, useWindowDimensions, Text } from 'react-native';
import { Colors } from '@/constants/colors';
import { StockChartData } from '@/types/stock.types';
// Provide Lightweight Charts directly from the bundle to the iframe to avoid CDN/CSP issues
// Note: For web iframe, load Lightweight Charts via a UMD script inside srcDoc or from /public.

export interface DrawingData {
  id: string;
  type: string;
  time1: number;
  price1: number;
  time2: number;
  price2: number;
  color: string;
  userId: string;
  userName: string;
  timestamp: number;
  groupId: string;
  symbol: string;
  properties?: Record<string, any> | null;
}

export interface CollaborativeTradingViewChartProps {
  symbol: string;
  groupId: string;
  userId: string;
  userName: string;
  chartData?: StockChartData[];
  isLoading?: boolean;
  onReady?: () => void;
  onDrawingCreate?: (drawing: DrawingData) => void;
  onDrawingUpdate?: (drawing: DrawingData) => void;
  onDrawingDelete?: (drawingId: string) => void;
  onRequestChangeSymbol?: (symbol: string) => void;
}

export interface CollaborativeTradingViewChartRef {
  addRemoteDrawing: (drawing: DrawingData) => void;
  updateRemoteDrawing: (drawing: DrawingData) => void;
  removeRemoteDrawing: (drawingId: string) => void;
  changeSymbol: (symbol: string) => void;
  clearAllDrawings: () => void;
}

const CollaborativeTradingViewChart = forwardRef<
  CollaborativeTradingViewChartRef,
  CollaborativeTradingViewChartProps
>(
  (
    {
      symbol,
      groupId,
      userId,
      userName,
      chartData,
      isLoading,
      onReady,
      onDrawingCreate,
      onDrawingUpdate,
      onDrawingDelete,
      onRequestChangeSymbol,
    },
    ref
  ) => {
    const { width, height } = useWindowDimensions();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const iframeWinRef = useRef<Window | null>(null);
    const readyRef = useRef(false);
    const [loading, setLoading] = useState(true);
    const pendingMessagesRef = useRef<any[]>([]);
    const drawingsStore = useRef(
      new Map<string, { local: DrawingData[]; remote: DrawingData[] }>()
    ); // host persistence

    const userColor = useMemo(() => {
      const colors = ['#3B82F6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
      const hash = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    }, [userId]);

    const postToIframe = useCallback((message: any) => {
      if (!iframeWinRef.current || !readyRef.current) {
        // Intentionally avoid queueing CLEAR_ALL_DRAWINGS to prevent accidental wipes when iframe wakes up
        if (message?.type !== 'CLEAR_ALL_DRAWINGS') {
          pendingMessagesRef.current.push(message);
        }
        return;
      }
      try {
        iframeWinRef.current.postMessage(message, '*');
      } catch {}
    }, []);

    const flushQueue = useCallback(() => {
      const q = pendingMessagesRef.current.splice(0);
      for (const m of q) {
        try {
          iframeWinRef.current?.postMessage(m, '*');
        } catch {}
      }
    }, []);

    const handleWindowMessage = useCallback(
      (event: MessageEvent) => {
        if (!iframeRef.current) return;
        // Accept messages and filter by known types instead of strict window source equality,
        // since about:srcdoc iframes can have cross‑realm identity in some browsers.
        const message =
          typeof event.data === 'string'
            ? (() => {
                try {
                  return JSON.parse(event.data);
                } catch {
                  return null;
                }
              })()
            : event.data;
        if (!message || typeof message !== 'object') return;
        // Debug: surface messages we handle
        if (message?.type) {
          try {
            console.log('[Web host] window message:', message.type);
          } catch {}
        }

        // Handle iframe console bridge (for debugging)
        if (message?.__fromChart) {
          if (message.type === 'IFRAME_ERROR') {
            console.error('[Chart iframe]', message.message);
          } else if (message.type === 'IFRAME_CONSOLE') {
            const fn = (console as any)[message.level] || console.log;
            fn('[Chart iframe]', message.message);
          }
          return;
        }

        switch (message?.type) {
          // From iframe â†’ lifecycle
          case 'CHART_READY':
            readyRef.current = true;
            setLoading(false);
            onReady?.();
            flushQueue();
            if (chartData?.length) {
              postToIframe({ type: 'LOAD_CHART_DATA', data: chartData });
            }
            break;

          // From iframe â†’ drawing events
          case 'DRAWING_CREATE':
            onDrawingCreate?.(message.data);
            break;
          case 'DRAWING_UPDATE':
            onDrawingUpdate?.(message.data);
            break;
          case 'DRAWING_DELETE':
            onDrawingDelete?.(message.data.id);
            break;
          case 'REQUEST_CHANGE_SYMBOL':
            if (message.symbol && typeof message.symbol === 'string') {
              onRequestChangeSymbol?.(String(message.symbol).toUpperCase());
            }
            break;

          // Host-managed persistence
          case 'REQUEST_SAVE': {
            const { key, payload } = message;
            if (typeof key === 'string') drawingsStore.current.set(key, payload);
            break;
          }
          case 'REQUEST_LOAD': {
            const { key } = message;
            const payload = (typeof key === 'string' ? drawingsStore.current.get(key) : null) || {
              local: [],
              remote: [],
            };
            try {
              iframeWinRef.current?.postMessage({ type: 'RESPONSE_LOAD', key, payload }, '*');
            } catch {}
            break;
          }
        }
      },
      [
        chartData,
        flushQueue,
        onReady,
        onDrawingCreate,
        onDrawingDelete,
        onDrawingUpdate,
        onRequestChangeSymbol,
        postToIframe,
      ]
    );

    useEffect(() => {
      window.addEventListener('message', handleWindowMessage);
      return () => window.removeEventListener('message', handleWindowMessage);
    }, [handleWindowMessage]);

    // Init payload
    useEffect(() => {
      if (iframeRef.current?.contentWindow) {
        iframeWinRef.current = iframeRef.current.contentWindow;
        const init = { type: 'INIT', userId, userName, groupId, userColor, symbol };
        postToIframe(init);
      }
    }, [groupId, userId, userName, userColor, symbol, postToIframe]);

    useEffect(() => {
      if (readyRef.current) return;
      const fallback = setTimeout(() => {
        if (readyRef.current) return;
        console.warn('[Web host] CHART_READY timeout fallback triggered');
        readyRef.current = true;
        setLoading(false);
        onReady?.();
        flushQueue();
        if (chartData?.length) {
          postToIframe({ type: 'LOAD_CHART_DATA', data: chartData });
        }
      }, 6000);
      return () => clearTimeout(fallback);
    }, [chartData, flushQueue, onReady, postToIframe]);

    // Push data when props change
    useEffect(() => {
      if (readyRef.current && chartData?.length) {
        postToIframe({ type: 'LOAD_CHART_DATA', data: chartData });
      }
    }, [chartData, postToIframe]);

    // Inline fallback HTML to avoid 404s when the dev server doesn't serve public/chart.html
    const iframeHtml = useMemo(
      () => `<!DOCTYPE html>
  <html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5" />
    <title>Collaborative Chart</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      html, body { width: 100%; height: 100%; background: #0a0e1a; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      #container { position: relative; width: 100%; height: 100%; overflow: hidden; }
      #chart-root { position: absolute; inset: 0; }
      #subchart-root { position: absolute; left: 0; right: 0; bottom: 0; height: 0; display: none; background: #0a0e1a; }
      #drawingCanvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10; }
      #info { position: absolute; bottom: 10px; left: 10px; background: rgba(30,41,59,0.95); color: #fff; padding: 10px 12px; border-radius: 8px; font-size: 12px; z-index: 100; backdrop-filter: blur(10px); }
      #loading-overlay { position: absolute; inset: 0; background: rgba(10,14,26,0.95); display: flex; align-items: center; justify-content: center; flex-direction: column; z-index: 200; color: #e5e7eb; }
    </style>
    <script src="/vendor/lightweight-charts.standalone.production.js"></script>
  </head>
  <body>
    <div id="container">
      <div id="chart-root"></div>
      <div id="subchart-root"></div>
      <canvas id="drawingCanvas"></canvas>
      <div id="info">
        <strong id="symbol-label">SYMBOL</strong>
        <span style="margin-left:6px;color:#9ca3af">Drag to pan â€¢ Scroll to zoom</span>
      </div>
      <div id="loading-overlay">
        <div style="color:#3b82f6;font-size:18px;font-weight:600;margin-bottom:8px">Loading chart...</div>
        <div style="color:#6b7280;font-size:12px">Waiting for data</div>
      </div>
    </div>
    <script>
    (function(){
      function sendConsole(payload){ try{ window.parent.postMessage({ __fromChart:true, ...payload }, '*'); }catch(e){} }
      function postHost(payload){ try{ window.parent.postMessage(payload, '*'); }catch(e){} }
      ['log','warn','error'].forEach(function(level){ var orig=console[level].bind(console); console[level]=function(){ var args=[].slice.call(arguments); try{ sendConsole({ type:'IFRAME_CONSOLE', level:level, message: args.map(a=>{ try { return typeof a==='object'? JSON.stringify(a): String(a) } catch(e){ return String(a) } }).join(' ') }); }catch(e){} orig.apply(console,args); } });

      // Diagnostics
      try { console.log('[Chart IFrame] LightweightCharts present?', typeof window.LightweightCharts); } catch(e) {}

      var chart=null, candleSeries=null; var bars=[]; var seriesKind='candlestick';
      // Minimal remote drawings support (DB-first persistence handled by host)
      var remoteDrawings=[]; // drawings pushed from host
      var canvas=document.getElementById('drawingCanvas');
      var ctx=canvas.getContext('2d');

      function resizeCanvas(){
        var dpr=window.devicePixelRatio||1; var w=window.innerWidth, h=window.innerHeight;
        canvas.width=Math.max(1, Math.floor(w*dpr)); canvas.height=Math.max(1, Math.floor(h*dpr));
        canvas.style.width=w+'px'; canvas.style.height=h+'px';
        ctx.setTransform(dpr,0,0,dpr,0,0);
        redrawDrawings();
      }

      function drawShape(d,color,width){ if(!chart||!candleSeries) return; var ts=chart.timeScale();
        var x1=ts.timeToCoordinate(d.time1); var y1=candleSeries.priceToCoordinate(d.price1);
        var x2=ts.timeToCoordinate(d.time2); var y2=candleSeries.priceToCoordinate(d.price2);
        if(x1==null||y1==null) return; ctx.strokeStyle=color||'#94a3b8'; ctx.lineWidth=width||2; ctx.lineCap='round'; ctx.lineJoin='round';
        if(d.type==='line'){ if(x2==null||y2==null) return; ctx.beginPath(); ctx.moveTo(x1,y1); ctx.lineTo(x2,y2); ctx.stroke(); }
        else if(d.type==='hline'){ ctx.beginPath(); ctx.moveTo(0,y1); ctx.lineTo(canvas.width,y1); ctx.stroke(); }
        else if(d.type==='vline'){ ctx.beginPath(); ctx.moveTo(x1,0); ctx.lineTo(x1,canvas.height); ctx.stroke(); }
      }

      function redrawDrawings(){ if(!ctx) return; ctx.clearRect(0,0,window.innerWidth,window.innerHeight); remoteDrawings.forEach(function(d){ drawShape(d,d.color,2); }); }
      function initChart(){
        var container=document.getElementById('chart-root'); var rect=container.getBoundingClientRect();
        chart=LightweightCharts.createChart(container,{
          width: Math.max(320, rect.width||window.innerWidth||320),
          height: Math.max(200, rect.height||window.innerHeight||200),
          layout:{ background:{ color:'#0a0e1a' }, textColor:'#d1d4dc' },
          grid:{ vertLines:{ color:'#1e293b' }, horzLines:{ color:'#1e293b' } },
          crosshair:{ mode:LightweightCharts.CrosshairMode.Normal },
          rightPriceScale:{ borderColor:'#2B2B43' },
          timeScale:{ borderColor:'#2B2B43', timeVisible:true, secondsVisible:false },
          handleScroll:{ mouseWheel:true, pressedMouseMove:true, horzTouchDrag:true, vertTouchDrag:true },
          handleScale:{ axisPressedMouseMove:true, mouseWheel:true, pinch:true }
        });
        try {
          if (chart && typeof chart.addCandlestickSeries === 'function') {
            seriesKind='candlestick';
            candleSeries=chart.addCandlestickSeries({ upColor:'#10b981', downColor:'#ef4444', borderUpColor:'#10b981', borderDownColor:'#ef4444', wickUpColor:'#10b981', wickDownColor:'#ef4444' });
          } else if (chart && typeof chart.addBarSeries === 'function') {
            seriesKind='bar';
            candleSeries=chart.addBarSeries({ upColor:'#10b981', downColor:'#ef4444' });
          } else if (chart && typeof chart.addLineSeries === 'function') {
            seriesKind='line';
            candleSeries=chart.addLineSeries({ color:'#3b82f6', lineWidth:2 });
          } else {
            console.error('Chart API not available'); sendConsole({ type:'IFRAME_ERROR', message:'Chart API not available' }); return;
          }
        } catch(e) {
          console.error('Chart API not available', e); sendConsole({ type:'IFRAME_ERROR', message:'Chart API not available' }); return;
        }
        chart.timeScale().fitContent();
        var overlay=document.getElementById('loading-overlay'); if(overlay) overlay.style.display='none';
        // redraw drawings on view changes and resize
        window.addEventListener('resize', function(){ var r=container.getBoundingClientRect(); chart.applyOptions({ width:r.width, height:r.height }); resizeCanvas(); });
        chart.timeScale().subscribeVisibleLogicalRangeChange(function(){ redrawDrawings(); });
        resizeCanvas();
        console.log('[Chart IFrame] Posting CHART_READY to host');
        postHost({ type:'CHART_READY' });
      }

      function handleMessage(message){
        if(!message||typeof message!=='object') return;
        switch(message.type){
          case 'INIT':
            var sym=message.symbol?String(message.symbol).toUpperCase():'SYMBOL';
            var el=document.getElementById('symbol-label'); if(el) el.textContent=sym;
            break;
          case 'LOAD_CHART_DATA':
            if(Array.isArray(message.data) && message.data.length>0){
              bars=message.data.map(function(pt){ return { time: Math.floor(new Date(pt.date).getTime()/1000), open: pt.open, high: pt.high, low: pt.low, close: pt.close }; });
              if(candleSeries){
                if(seriesKind==='line'){
                  var lineData = bars.map(function(b){ return { time: b.time, value: b.close }; });
                  candleSeries.setData(lineData);
                } else {
                  candleSeries.setData(bars);
                }
              }
              if(window.chart && window.chart.timeScale){ chart.timeScale().fitContent(); }
              setTimeout(redrawDrawings, 0);
            }
            break;
          case 'ADD_DRAWING': {
            var d=message.data; if(!d||!d.id) break;
            var exist=remoteDrawings.some(function(x){return x.id===d.id;});
            if(!exist){ remoteDrawings.push(d); redrawDrawings(); }
            break; }
          case 'UPDATE_DRAWING': {
            var u=message.data; if(!u||!u.id) break;
            var idx=remoteDrawings.findIndex(function(x){return x.id===u.id;});
            if(idx>=0){ remoteDrawings[idx]=u; redrawDrawings(); } else { remoteDrawings.push(u); redrawDrawings(); }
            break; }
          case 'REMOVE_DRAWING': {
            var id=message.data && message.data.id; if(!id) break;
            remoteDrawings = remoteDrawings.filter(function(x){return x.id!==id;}); redrawDrawings(); break; }
          case 'CLEAR_ALL_DRAWINGS': {
            remoteDrawings=[]; redrawDrawings(); break; }
        }
      }
      window.addEventListener('message', function(event){ var msg=typeof event.data==='string'? JSON.parse(event.data): event.data; if(msg && !msg.__fromChart) handleMessage(msg); });
      (function waitLib(i){ if(window.LightweightCharts && typeof LightweightCharts.createChart==='function'){ initChart(); } else if(i<60){ setTimeout(function(){ waitLib(i+1); }, 50); } else { console.error('LightweightCharts not loaded'); sendConsole({ type:'IFRAME_ERROR', message:'LightweightCharts not loaded' }); } })(0);
    })();
    </script>
  </body>
  </html>`,
      [symbol]
    );

    useImperativeHandle(
      ref,
      () => ({
        addRemoteDrawing: (drawing: DrawingData) =>
          postToIframe({ type: 'ADD_DRAWING', data: drawing }),
        updateRemoteDrawing: (drawing: DrawingData) =>
          postToIframe({ type: 'UPDATE_DRAWING', data: drawing }),
        removeRemoteDrawing: (drawingId: string) =>
          postToIframe({ type: 'REMOVE_DRAWING', data: { id: drawingId } }),
        changeSymbol: (newSymbol: string) =>
          postToIframe({ type: 'CHANGE_SYMBOL', data: { symbol: newSymbol } }),
        clearAllDrawings: () => postToIframe({ type: 'CLEAR_ALL_DRAWINGS' }),
      }),
      [postToIframe]
    );

    return (
      <View style={[styles.container, { width, height }]}>
        {loading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={Colors.accent.primary} />
            <Text style={styles.loadingText}>Initializing chart...</Text>
          </View>
        )}
        <iframe
          ref={iframeRef}
          srcDoc={iframeHtml}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
        />
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Colors.background.primary },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
    zIndex: 999,
  },
  loadingText: { marginTop: 12, fontSize: 14, color: Colors.text.secondary },
  comingSoonContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
    paddingHorizontal: 16,
  },
  comingSoonTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  comingSoonSubtitle: {
    marginTop: 8,
    fontSize: 16,
    color: Colors.text.secondary,
  },
});

CollaborativeTradingViewChart.displayName = 'CollaborativeTradingViewChart.web';
export default CollaborativeTradingViewChart;
